import { DatePipe } from '@angular/common';
import { effect, inject, Injectable, signal } from '@angular/core';
import { NotificacaoService } from '@nomoh-monorepo/data-access-shared-notificacao';
import { EnvironmentService } from '@nomoh-monorepo/shared-data-access-environment';
import { io, Socket } from 'socket.io-client';
import { EstabelecimentoDataService } from '../feature-estabelecimento/estabelecimento.dataservice';
import { AgendaService } from './agenda.service';

@Injectable({ providedIn: 'root' })
export class AgendaSocketService {
  private environmentService = inject(EnvironmentService);
  private estabelecimentoDataService = inject(EstabelecimentoDataService);
  private agendaService = inject(AgendaService);
  private notificacaoService = inject(NotificacaoService);

  private socket: Socket | null = null;
  private socketUrl: string;
  private _isActive = signal(false);

  constructor() {
    this.socketUrl = this.resolveSocketUrl();

    // Effect para escutar mudanças no estabelecimento responsivo a troca de contas
    effect(() => {
      const isAtivo = this._isActive();
      const estabelecimento =
        this.estabelecimentoDataService.$estabelecimentoAtual();

      const telefonesEncontrados: string[] = [];
      if (estabelecimento?.telefone)
        telefonesEncontrados.push(estabelecimento.telefone);
      // Aqui o estabelecimento possivelmente já tem ou terá o numero de celular / zap separado no futuro:
      if ((estabelecimento as any)?.celular)
        telefonesEncontrados.push((estabelecimento as any).celular);
      if ((estabelecimento as any)?.telefoneWhatsapp)
        telefonesEncontrados.push((estabelecimento as any).telefoneWhatsapp);

      if (isAtivo && telefonesEncontrados.length > 0) {
        this.conectar(telefonesEncontrados);
      } else {
        this.desconectar();
      }
    });
  }

  iniciar(): void {
    this._isActive.set(true);
  }

  parar(): void {
    this._isActive.set(false);
  }

  private conectar(telefones: string[]): void {
    if (!telefones || telefones.length === 0) return;

    // Normalização: remove tudo que não for número e remove o "55" inicial se existir
    const telefonesNormalizados = telefones
      .map((t) => {
        let clean = t.replace(/\D/g, '');
        if (clean.startsWith('55') && clean.length > 11)
          clean = clean.substring(2);
        return clean;
      })
      .filter((t) => t.length >= 10); // só envia válidos

    // Cria as salas unindo-as pra poder passar em QueryString simple
    const salasId = telefonesNormalizados.join(',');

    if (this.socket) {
      if (this.socket.io.opts.query?.['telefones'] === salasId) {
        return; // Já conectado nas mesmas salas
      } else {
        this.desconectar(); // Mudou (ex: troca de empresas), reconecta
      }
    }

    this.socket = io(this.socketUrl, {
      transports: ['websocket', 'polling'], // Adicionado polling como fallback de WebSockets direct failure CORS
      forceNew: true, // Força nova conexão limpa
      reconnection: true,
      reconnectionAttempts: 10,
      query: { telefones: salasId },
    });

    this.socket.on('connect', () => { });

    this.socket.on(
      'novoAgendamentoWhatsapp',
      (data: { nome: string; horario: string | Date }) => {
        if (data?.nome && data?.horario) {
          const datePipe = new DatePipe('pt-BR');
          const dataFormatada = datePipe.transform(data.horario, 'dd/MM/yyyy');
          const horaFormatada = datePipe.transform(data.horario, 'HH:mm');

          this.notificacaoService.notificacaoInfo(
            `Novo agendamento recebido via WhatsApp!\nCliente: ${data.nome}\nData: ${dataFormatada} às ${horaFormatada}`
          );
        }

        // Quando receber um novo agendamento, recarrega a agenda
        this.agendaService.buscarAgenda().catch((err) => {
          console.error('Erro ao recarregar agenda via socket', err);
        });
      }
    );

    this.socket.on(
      'confirmarAgendamentoWhatsapp',
      (data: { nome: string; horario: string | Date }) => {
        if (data?.nome && data?.horario) {
          const datePipe = new DatePipe('pt-BR');
          const dataFormatada = datePipe.transform(data.horario, 'dd/MM/yyyy');
          const horaFormatada = datePipe.transform(data.horario, 'HH:mm');

          this.notificacaoService.notificacaoInfo(
            `Agendamento confirmado via WhatsApp!\nCliente: ${data.nome}\nData: ${dataFormatada} às ${horaFormatada}`
          );
        }

        // Quando receber um agendamento confirmado, recarrega a agenda
        this.agendaService.buscarAgenda().catch((err) => {
          console.error('Erro ao recarregar agenda via socket', err);
        });
      }
    );

    this.socket.on(
      'cancelarAgendamentoWhatsapp',
      (data: { nome: string; horario: string | Date }) => {
        if (data?.nome && data?.horario) {
          const datePipe = new DatePipe('pt-BR');
          const dataFormatada = datePipe.transform(data.horario, 'dd/MM/yyyy');
          const horaFormatada = datePipe.transform(data.horario, 'HH:mm');

          this.notificacaoService.notificacaoInfo(
            `Agendamento cancelado via WhatsApp!\nCliente: ${data.nome}\nData: ${dataFormatada} às ${horaFormatada}`
          );
        }

        // Quando receber um agendamento cancelado, recarrega a agenda
        this.agendaService.buscarAgenda().catch((err) => {
          console.error('Erro ao recarregar agenda via socket', err);
        });
      }
    );

    this.socket.on('disconnect', () => { });

    this.socket.on('connect_error', (error) => {
      console.error('⚠️ Erro de conexão no AgendaSocketService:', error);
    });
  }

  private desconectar(): void {
    if (this.socket) {
      this.socket.removeAllListeners();
      this.socket.disconnect();
      this.socket = null;
    }
  }

  private resolveSocketUrl(): string {
    const bardoApiUrl = this.environmentService.getApiUrl('bardo');
    if (!bardoApiUrl) {
      return 'http://localhost:3001';
    }

    try {
      const url = new URL(bardoApiUrl);
      return `${url.protocol}//${url.host}`;
    } catch (error) {
      console.warn(
        'Não foi possível interpretar apiUrls.bardo; usando fallback.',
        error
      );
      return 'http://localhost:3001';
    }
  }
}
